/**
 * Collection List Slider Web Component - Sleek Theme OS 2.0
 * Smooth horizontal carousel with touch/pointer drag, loop support,
 * and responsive snapping matching https://webbiewooble.github.io/Sleekprototype/
 */
if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragOffset = 0;
      this.hasDragged = false;
      this.resizeTimer = null;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`) || this.closest('.section-collection-list') || this.parentElement;
      this.itemsWrapper = this.querySelector('.collection-list__items') || this.querySelector('.swiper-wrapper');
      this.slides = Array.from(this.querySelectorAll('.swiper-slide'));
      this.enableSlider = this.dataset.enableSlider !== 'false';
      this.isLoop = this.dataset.loop !== 'false';
      this.itemsPerView = parseInt(this.dataset.items) || 3;

      if (!this.enableSlider || !this.itemsWrapper || this.slides.length === 0) return;

      this.prevBtn = this.section?.querySelector('.swiper-btn-prev');
      this.nextBtn = this.section?.querySelector('.swiper-btn-next');

      this.initSlider();
      this.initEvents();
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    initSlider() {
      // Ensure classes for swiper styling
      this.classList.add('swiper', 'collection-list--initialized');
      this.updateMeasurements();
      this.updatePosition(false);
      this.updateNavButtons();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => {
            this.updateMeasurements();
            this.updatePosition(false);
          }, 100);
        });
        this.resizeObserver.observe(this);
      }
    }

    updateMeasurements() {
      if (!this.slides.length || !this.itemsWrapper) return;
      const firstSlide = this.slides[0];
      const rect = firstSlide.getBoundingClientRect();
      this.slideWidth = rect.width || 340;
      
      // Calculate gap between first two slides
      if (this.slides.length > 1) {
        const secondRect = this.slides[1].getBoundingClientRect();
        this.slideGap = Math.max(0, Math.round(secondRect.left - rect.right));
      } else {
        this.slideGap = 24;
      }
      if (!this.slideGap) this.slideGap = 24;

      this.stepDistance = this.slideWidth + this.slideGap;
      this.containerWidth = this.getBoundingClientRect().width || (this.parentElement?.getBoundingClientRect().width ?? 800);
      
      // Total track scrollable width
      this.totalTrackWidth = (this.slides.length * this.slideWidth) + ((this.slides.length - 1) * this.slideGap);
      this.maxOffset = Math.max(0, this.totalTrackWidth - this.containerWidth);
      
      // Maximum logical slide index
      const visibleCount = Math.max(1, Math.floor(this.containerWidth / this.stepDistance));
      this.maxIndex = Math.max(0, this.slides.length - visibleCount);
      if (this.maxIndex === 0 && this.slides.length > 1) {
        this.maxIndex = this.slides.length - 1;
      }
    }

    initEvents() {
      // Prev / Next button navigation
      this.prevBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.slidePrev();
      });

      this.nextBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.slideNext();
      });

      // Unified Pointer Events (handles mouse and touch seamlessly with 1:1 tracking)
      this.itemsWrapper.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      window.addEventListener('pointermove', (e) => this.onPointerMove(e));
      window.addEventListener('pointerup', (e) => this.onPointerUp(e));
      window.addEventListener('pointercancel', (e) => this.onPointerUp(e));

      // Prevent link navigation if the user was actively dragging
      this.itemsWrapper.addEventListener('click', (e) => {
        if (this.hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          this.hasDragged = false;
        }
      }, true);

      // Keyboard navigation
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') {
          this.slideNext();
        } else if (e.key === 'ArrowLeft') {
          this.slidePrev();
        }
      });

      // Shopify Admin Theme Editor block selection
      document.addEventListener('shopify:block:select', (e) => {
        if (e.detail.sectionId !== this.sectionId) return;
        const index = Number(e.target.dataset.index);
        if (!isNaN(index)) this.slideTo(index);
      });
    }

    slideNext() {
      this.updateMeasurements();
      if (this.isLoop) {
        if (this.currentIndex >= this.slides.length - 1) {
          this.currentIndex = 0;
        } else {
          this.currentIndex++;
        }
      } else {
        this.currentIndex = Math.min(this.currentIndex + 1, this.slides.length - 1);
      }
      this.updatePosition(true);
      this.updateNavButtons();
    }

    slidePrev() {
      this.updateMeasurements();
      if (this.isLoop) {
        if (this.currentIndex <= 0) {
          this.currentIndex = this.slides.length - 1;
        } else {
          this.currentIndex--;
        }
      } else {
        this.currentIndex = Math.max(this.currentIndex - 1, 0);
      }
      this.updatePosition(true);
      this.updateNavButtons();
    }

    slideTo(index) {
      this.updateMeasurements();
      this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.updatePosition(true);
      this.updateNavButtons();
    }

    updatePosition(animate = true) {
      if (!this.itemsWrapper || this.slides.length === 0) return;
      
      const step = this.stepDistance || (this.slides[0].offsetWidth + 24);
      let targetOffset = this.currentIndex * step;

      // Ensure we don't overscroll into empty void when near the end (if not looping)
      if (!this.isLoop && this.maxOffset > 0 && targetOffset > this.maxOffset) {
        targetOffset = this.maxOffset;
      }

      this.currentOffset = targetOffset;
      this.itemsWrapper.style.transition = animate ? 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)' : 'none';
      this.itemsWrapper.style.transform = `translate3d(-${targetOffset}px, 0, 0)`;
    }

    updateNavButtons() {
      if (this.isLoop) {
        this.prevBtn?.removeAttribute('disabled');
        this.nextBtn?.removeAttribute('disabled');
        return;
      }
      if (this.prevBtn) {
        if (this.currentIndex <= 0) {
          this.prevBtn.setAttribute('disabled', 'disabled');
        } else {
          this.prevBtn.removeAttribute('disabled');
        }
      }
      if (this.nextBtn) {
        if (this.currentIndex >= this.slides.length - 1) {
          this.nextBtn.setAttribute('disabled', 'disabled');
        } else {
          this.nextBtn.removeAttribute('disabled');
        }
      }
    }

    onPointerDown(e) {
      // Only primary mouse button or touch
      if (e.button !== 0 && e.pointerType === 'mouse') return;

      this.isDragging = true;
      this.hasDragged = false;
      this.startX = e.clientX;
      this.currentX = e.clientX;
      this.dragOffset = 0;

      this.itemsWrapper.classList.add('is-dragging');
      this.itemsWrapper.style.transition = 'none';

      try {
        this.itemsWrapper.setPointerCapture(e.pointerId);
      } catch (err) {
        // Fallback for environments where setPointerCapture isn't supported
      }
    }

    onPointerMove(e) {
      if (!this.isDragging) return;

      this.currentX = e.clientX;
      this.dragOffset = this.currentX - this.startX;

      if (Math.abs(this.dragOffset) > 8) {
        this.hasDragged = true;
      }

      // 1:1 real-time displacement
      const step = this.stepDistance || (this.slides[0].offsetWidth + 24);
      const baseOffset = this.currentIndex * step;
      const currentTranslate = baseOffset - this.dragOffset;

      this.itemsWrapper.style.transform = `translate3d(-${currentTranslate}px, 0, 0)`;
    }

    onPointerUp(e) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.itemsWrapper.classList.remove('is-dragging');

      try {
        this.itemsWrapper.releasePointerCapture(e.pointerId);
      } catch (err) {}

      const diff = this.dragOffset;
      const threshold = 45;

      if (diff < -threshold) {
        this.slideNext();
      } else if (diff > threshold) {
        this.slidePrev();
      } else {
        // Snap back to current slide position
        this.updatePosition(true);
      }

      this.dragOffset = 0;
    }
  }

  customElements.define('collection-list', CollectionList);
}
